import { LinkBreak } from '@phosphor-icons/react'
import get from 'lodash/get'
import PropTypes from 'prop-types'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import { serviceOptions, MODAL_MODE } from '../helpers'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import API from '../../../../api/api'
import {
  disconnectProvider,
  selectProviders,
} from '../../../../reducers/reputationManagementReducer'

const Connections = ({
  buildingId,
  providers,
  onNext = () => {},
  disconnectProvider = () => {},
}) => {
  const { t } = useTranslation(['organisation'])
  const [providerLoading, setProviderLoading] = useState(
    serviceOptions.reduce((acc, curr) => {
      acc[curr.value] = false
      return acc
    }, {}),
  )

  const { displaySnackbar } = useSnackbar()

  const isConnected = useCallback(
    (provider) => {
      return providers.some((p) => p.provider === provider && p.available)
    },
    [providers],
  )

  const getLocationName = useCallback(
    (provider) =>
      get(
        providers.find((p) => p.provider === provider),
        'locationName',
        '',
      ),
    [providers],
  )

  const getPlatformAuthUrl = async (provider) => {
    if (provider === 'apartments') {
      onNext(MODAL_MODE.APARTMENTS)
      return
    }

    try {
      setProviderLoading((prev) => ({ ...prev, [provider]: true }))
      const { data } = await API.getProviderBusinessAuthUrl(buildingId, provider)

      if (data.authUrl) {
        const popup = window.open(
          data.authUrl,
          'popup=yes,width=500,height=600,left=' +
            (window.innerWidth / 2 - 250) +
            ',top=' +
            (window.innerHeight / 2 - 300) +
            ',scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no',
        )

        const handleMessage = (event) => {
          if (event.origin !== window.location.origin) {
            return
          }

          if (event.data.type === 'OAUTH_SUCCESS') {
            displaySnackbar(
              t('organisation:reputationManagement.connections.toasts.connectedTo', { provider }),
              false,
            )
            popup.close()
            setProviderLoading((prev) => ({ ...prev, [provider]: false }))
            window.removeEventListener('message', handleMessage)
            onNext(MODAL_MODE.LOCATIONS)
          } else if (event.data.type === 'OAUTH_ERROR') {
            displaySnackbar(
              t('organisation:reputationManagement.connections.errors.connectFailed'),
              true,
            )
            popup.close()
            setProviderLoading((prev) => ({ ...prev, [provider]: false }))
            window.removeEventListener('message', handleMessage)
          }
        }

        window.addEventListener('message', handleMessage)

        // Fallback: Check if popup was closed manually
        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed)
            window.removeEventListener('message', handleMessage)
            setProviderLoading((prev) => ({ ...prev, [provider]: false }))
          }
        }, 1000)
      }
    } catch (error) {
      console.error(error)
      displaySnackbar(t('organisation:reputationManagement.connections.errors.connectFailed'), true)
      setProviderLoading((prev) => ({ ...prev, [provider]: false }))
    }
  }

  const handleDisconnect = async (provider) => {
    try {
      setProviderLoading((prev) => ({ ...prev, [provider]: true }))
      await disconnectProvider(buildingId, provider)
    } catch (error) {
      console.error(error)
      displaySnackbar(
        t('organisation:reputationManagement.connections.errors.disconnectFailed'),
        true,
      )
    } finally {
      setProviderLoading((prev) => ({ ...prev, [provider]: false }))
    }
  }

  return (
    <React.Fragment>
      <h3 className="reputation-management-page__settings--title">
        {t('organisation:reputationManagement.connections.title')}
      </h3>
      <div className="reputation-management-page__box-divider" />
      {serviceOptions.map((platform) => (
        <React.Fragment key={platform.id}>
          <div className="reputation-management-page__settings--platform">
            <div className="reputation-management-page__settings--company">
              <img src={platform.iconPng} alt={platform.label} />
              <div>
                <h4>{platform.label}</h4>
                {<p>{getLocationName(platform.value)}</p>}
              </div>
            </div>
            <div className="reputation-management-page__settings--connect">
              <button
                onClick={() => getPlatformAuthUrl(platform.value)}
                className={`button button-black ${
                  providerLoading[platform.value] ? 'is-loading' : ''
                }`}
                disabled={isConnected(platform.value)}
              >
                {isConnected(platform.value)
                  ? t('organisation:reputationManagement.connections.connected')
                  : t('organisation:reputationManagement.apartmentsConnectionModal.connect')}
              </button>
              {isConnected(platform.value) && (
                <div
                  className="reputation-dashboard-tab__go-to-btn"
                  onClick={() => handleDisconnect(platform.value)}
                >
                  <LinkBreak size={16} />
                  {t('organisation:reputationManagement.connections.disconnect')}
                </div>
              )}
            </div>
          </div>
          <div className="reputation-management-page__box-divider" />
        </React.Fragment>
      ))}
    </React.Fragment>
  )
}

Connections.propTypes = {
  buildingId: PropTypes.number.isRequired,
  onNext: PropTypes.func.isRequired,
  providers: PropTypes.array.isRequired,
  disconnectProvider: PropTypes.func.isRequired,
}

export default connect(
  (state) => ({
    providers: selectProviders(state),
  }),
  { disconnectProvider },
)(Connections)
